/**
 * PDP size drawer — syncs list rows, closes on select, updates the trigger label with chosen size.
 *
 * Also owns the add-to-cart size gate: until the customer picks a size, the buy buttons read
 * "choose size" and every path into the cart is blocked.
 */
(function () {
  'use strict';

  function valueElForDrawerId(drawerId) {
    return (
      document.querySelector('[data-pdp-size-drawer-value="' + drawerId + '"]') ||
      document.getElementById(drawerId + '-value')
    );
  }

  function triggerForDrawerId(drawerId) {
    return document.querySelector('[data-pdp-size-drawer-id="' + drawerId + '"]');
  }

  function setTriggerSize(drawerId, sizeText) {
    var text = (sizeText || '').trim();
    var valueEl = valueElForDrawerId(drawerId);
    var trigger = triggerForDrawerId(drawerId);

    if (valueEl) {
      valueEl.textContent = text;
      valueEl.classList.toggle('is-visible', text.length > 0);
    }

    if (trigger) {
      trigger.classList.toggle('has-selected-size', text.length > 0);
      var labelEl = trigger.querySelector('.product-size-drawer__trigger-label');
      if (text.length > 0 && labelEl) {
        trigger.setAttribute('aria-label', labelEl.textContent.trim() + ', ' + text);
      } else {
        trigger.removeAttribute('aria-label');
      }
    }
  }

  function syncTriggerFromInput(input) {
    if (!input || !input.checked) return;

    var drawer = input.closest('x-drawer.drawer--pdp-size, .drawer--pdp-size');
    var drawerId = drawer && drawer.id;
    if (!drawerId && input.form) {
      var trigger = input.form.querySelector('[data-pdp-size-drawer-id]');
      drawerId = trigger && trigger.getAttribute('data-pdp-size-drawer-id');
    }

    if (!drawerId) return;

    var label = input.closest('label');
    var sizeLabel = label && label.querySelector('.pdp-size-drawer__size-label');
    var sizeText = sizeLabel ? sizeLabel.textContent.trim() : '';

    if (!sizeText && input.labels && input.labels[0]) {
      var labelEl = input.labels[0].querySelector('.pdp-size-drawer__size-label');
      sizeText = labelEl ? labelEl.textContent.trim() : '';
    }

    setTriggerSize(drawerId, sizeText);
  }

  function syncAllTriggers(scope) {
    (scope || document).querySelectorAll('[data-pdp-size-drawer-id]').forEach(function (trigger) {
      var drawerId = trigger.getAttribute('data-pdp-size-drawer-id');
      var valueEl = valueElForDrawerId(drawerId);
      var text = valueEl ? valueEl.textContent.trim() : '';

      if (text) {
        setTriggerSize(drawerId, text);
        return;
      }

      var drawer = document.getElementById(drawerId);
      var checked = drawer && drawer.querySelector('.pdp-size-drawer__radio:checked');
      if (checked) {
        syncTriggerFromInput(checked);
      }
    });
  }

  function syncRowSelectedStates(list) {
    if (!list) return;

    list.querySelectorAll('label.pdp-size-drawer__row').forEach(function (row) {
      var input = row.querySelector('.pdp-size-drawer__radio');
      row.classList.toggle('pdp-size-drawer__row--selected', !!(input && input.checked));
    });

    list.querySelectorAll('.pdp-size-drawer__row-outer').forEach(function (outer) {
      var input = outer.querySelector('.pdp-size-drawer__radio:checked');
      var link = outer.querySelector('a.pdp-size-drawer__row');
      if (link) {
        link.classList.toggle('pdp-size-drawer__row--selected', !!input);
      }
    });
  }

  function syncDrawerListFromTemplate(template) {
    var drawerId = template.getAttribute('data-pdp-size-drawer-template');
    if (!drawerId) return;

    var drawer = document.getElementById(drawerId);
    if (!drawer) return;

    var list = drawer.querySelector('[data-pdp-size-drawer-list]');
    if (!list) return;

    list.innerHTML = template.innerHTML;
    syncRowSelectedStates(list);

    var checked = list.querySelector('.pdp-size-drawer__radio:checked');
    if (checked) {
      syncTriggerFromInput(checked);
    }
  }

  function syncDrawerLists(scope) {
    (scope || document).querySelectorAll('[data-pdp-size-drawer-template]').forEach(syncDrawerListFromTemplate);
    syncAllTriggers(scope);
  }

  function closeOpenSizeDrawers() {
    document.querySelectorAll('x-drawer.drawer--pdp-size[open]').forEach(function (drawer) {
      if (typeof drawer.hide === 'function') {
        drawer.hide();
      } else {
        drawer.removeAttribute('open');
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Add-to-cart size gate
   * ------------------------------------------------------------------ */

  // formId -> 'pending' | 'resolved'. Kept out of the DOM because <product-rerender>
  // replaces <buy-buttons> (main column) and the whole container (sticky bar) on every
  // variant change. Once a form is resolved it never goes back to pending.
  var gateState = new Map();

  function isGatePending(formId) {
    return gateState.get(formId) === 'pending';
  }

  /**
   * When the gate is resolved, the chosen size must look activated. Quick-shop Liquid still paints
   * sizes as unselected on every re-render (it cannot see option_values=), so re-check from the
   * picker's current variant JSON after pending is cleared.
   */
  function restoreCheckedSizeFromVariant(formId, picker) {
    if (!picker) return;

    var sizeFieldset = picker.querySelector('[data-pdp-size-option]');
    if (!sizeFieldset) return;

    var variantScript = picker.querySelector('script[data-variant]');
    if (!variantScript) return;

    var variant;
    try {
      variant = JSON.parse(variantScript.textContent || '{}');
    } catch (e) {
      return;
    }

    var optionNames = Array.isArray(variant.options)
      ? variant.options.map(String)
      : [variant.option1, variant.option2, variant.option3].filter(Boolean).map(String);

    if (!optionNames.length) return;

    function labelTextForInput(input) {
      var label =
        (input.labels && input.labels[0]) ||
        (input.id && document.querySelector('label[for="' + CSS.escape(input.id) + '"]')) ||
        input.nextElementSibling;

      if (!label) return '';
      return (label.textContent || '').replace(/\s+/g, ' ').trim();
    }

    var checkedExisting = sizeFieldset.querySelector('input[data-option-position]:checked');
    if (!checkedExisting) {
      sizeFieldset.querySelectorAll('input[data-option-position]').forEach(function (input) {
        if (input.disabled) return;
        if (optionNames.indexOf(labelTextForInput(input)) === -1) return;
        input.checked = true;
      });
    }

    document.querySelectorAll('.drawer--pdp-size [data-pdp-size-drawer-list]').forEach(function (list) {
      var sample = list.querySelector('.pdp-size-drawer__radio[form="' + formId + '"]');
      if (!sample) return;

      if (!list.querySelector('.pdp-size-drawer__radio:checked')) {
        list.querySelectorAll('.pdp-size-drawer__radio').forEach(function (input) {
          if (input.disabled) return;
          var row = input.closest('label, .pdp-size-drawer__row-outer');
          var textEl = row && row.querySelector('.pdp-size-drawer__size-label');
          var text = textEl ? textEl.textContent.trim() : '';
          if (optionNames.indexOf(text) !== -1) {
            input.checked = true;
          }
        });
      }

      syncRowSelectedStates(list);
    });
  }

  function applyGateState(formId) {
    var pending = isGatePending(formId);
    var picker = document.querySelector('variant-picker[form-id="' + formId + '"]');
    var hideDefaultSelectedStyle = !!(picker && picker.hasAttribute('data-hide-default-size-selected-style'));

    document.querySelectorAll('buy-buttons[form="' + formId + '"][data-size-gate-label]').forEach(function (buyButtons) {
      var button = buyButtons.querySelector('button[type="submit"]');
      var label = pending
        ? buyButtons.getAttribute('data-size-gate-pending-label')
        : buyButtons.getAttribute('data-size-gate-label');

      if (pending) {
        buyButtons.setAttribute('data-size-pending', '');
      } else {
        buyButtons.removeAttribute('data-size-pending');
      }

      if (button && label) {
        button.textContent = label;
      }
    });

    // The picker itself stays untouched — it is still legitimately sitting on variant #1 — but
    // nothing on screen may claim a size has been chosen while the button says otherwise.
    if (!pending) {
      if (picker) {
        picker.querySelectorAll('[data-pdp-size-option]').forEach(function (fieldset) {
          fieldset.removeAttribute('data-size-selection-pending');
        });
      }

      document.querySelectorAll('variant-picker[form-id="' + formId + '"] [data-pdp-size-drawer-id]').forEach(function (trigger) {
        var drawerId = trigger.getAttribute('data-pdp-size-drawer-id');
        var drawer = drawerId && document.getElementById(drawerId);
        if (drawer) {
          drawer.removeAttribute('data-size-selection-pending');
          syncRowSelectedStates(drawer.querySelector('[data-pdp-size-drawer-list]'));
        }
      });

      // After resolve, Liquid/JS may have left size radios unchecked (quick shop re-render).
      // Re-check the size that matches the picker's current variant so the activated style shows.
      restoreCheckedSizeFromVariant(formId, picker);

      syncAllTriggers(document);
      return;
    }

    document.querySelectorAll('variant-picker[form-id="' + formId + '"] [data-pdp-size-drawer-id]').forEach(function (trigger) {
      var drawerId = trigger.getAttribute('data-pdp-size-drawer-id');
      setTriggerSize(drawerId, '');

      var drawer = document.getElementById(drawerId);
      if (!drawer) return;

      if (hideDefaultSelectedStyle) {
        drawer.setAttribute('data-size-selection-pending', '');
        drawer.querySelectorAll('.pdp-size-drawer__radio:checked').forEach(function (input) {
          input.checked = false;
        });
        drawer.querySelectorAll('.pdp-size-drawer__row--selected').forEach(function (row) {
          row.classList.remove('pdp-size-drawer__row--selected');
        });
      } else {
        drawer.removeAttribute('data-size-selection-pending');
      }
    });

    if (picker && hideDefaultSelectedStyle) {
      picker.querySelectorAll('[data-pdp-size-option]').forEach(function (fieldset) {
        fieldset.setAttribute('data-size-selection-pending', '');
        fieldset.querySelectorAll('input[data-option-position]:checked').forEach(function (input) {
          input.checked = false;
        });
      });
    } else if (picker) {
      picker.querySelectorAll('[data-pdp-size-option][data-size-selection-pending]').forEach(function (fieldset) {
        fieldset.removeAttribute('data-size-selection-pending');
      });
    }
  }

  /**
   * Pick up buy buttons rendered as pending and re-assert the known state over whatever
   * Liquid just painted. Safe to call as often as needed.
   */
  function refreshGates() {
    document.querySelectorAll('buy-buttons[data-size-gate-label]').forEach(function (buyButtons) {
      var formId = buyButtons.getAttribute('form');
      if (!formId) return;

      if (!gateState.has(formId) && buyButtons.hasAttribute('data-size-pending')) {
        gateState.set(formId, 'pending');
      }

      applyGateState(formId);
    });
  }

  function resolveGate(formId) {
    if (!formId || !isGatePending(formId)) return;

    clearBrowserSizePrompt(formId);
    gateState.set(formId, 'resolved');
    applyGateState(formId);
  }

  function pendingSizeMessage(formId) {
    var buyButtons = document.querySelector('buy-buttons[form="' + formId + '"][data-size-gate-pending-label]');
    var message = buyButtons && buyButtons.getAttribute('data-size-gate-pending-label');
    return message && message.trim() ? message.trim() : 'Choose size';
  }

  function clearBrowserSizePrompt(formId) {
    var scope = formId ? document.querySelector('variant-picker[form-id="' + formId + '"]') : document;
    if (!scope) return;

    scope.querySelectorAll('[data-size-gate-prompt]').forEach(function (input) {
      input.setCustomValidity('');
      input.remove();
    });
    scope.querySelectorAll('.is-size-gate-prompting').forEach(function (fieldset) {
      fieldset.classList.remove('is-size-gate-prompting');
    });
  }

  /**
   * Native validation bubble anchored to the size row.
   * Size radios use .sr-only, so the browser would otherwise pin the tooltip
   * to a clipped 1px box. A text field (not a radio) is what shows the bubble
   * reliably on desktop and on phones; inputmode=none keeps the keyboard closed.
   */
  function showBrowserSizePrompt(sizeFieldset, message) {
    sizeFieldset.classList.add('is-size-gate-attention', 'is-size-gate-prompting');
    sizeFieldset.scrollIntoView({ behavior: 'auto', block: 'center' });

    var input = sizeFieldset.querySelector('[data-size-gate-prompt]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'text';
      input.required = true;
      input.tabIndex = -1;
      input.autocomplete = 'off';
      input.value = '';
      input.setAttribute('inputmode', 'none');
      input.setAttribute('data-size-gate-prompt', '');
      // Only unbind when the sizes sit inside a form. A bogus form id on a
      // form-less control can stop the browser from painting the bubble.
      if (sizeFieldset.closest('form')) {
        input.setAttribute('form', 'pdp-size-gate-prompt');
      }
      sizeFieldset.appendChild(input);
    }

    input.setCustomValidity(message);
    if (typeof input.reportValidity === 'function') {
      input.reportValidity();
    }

    window.setTimeout(function () {
      sizeFieldset.classList.remove('is-size-gate-attention');
    }, 1200);
  }

  function sizePromptUsesDrawer(formId) {
    var picker = document.querySelector('variant-picker[form-id="' + formId + '"]');
    if (picker && picker.querySelector('[data-product-size-drawer-trigger]')) return true;

    var radio = document.querySelector('.drawer--pdp-size .pdp-size-drawer__radio[form="' + formId + '"]');
    var drawer = radio && radio.closest('.drawer--pdp-size');
    return !!(drawer && typeof drawer.show === 'function');
  }

  function promptForSize(formId) {
    // Drawer must open after this click finishes, or the focus trap reads it as an
    // outside click and closes immediately. The inline bubble has to run in the
    // gesture itself or some browsers will not paint it.
    if (sizePromptUsesDrawer(formId)) {
      window.requestAnimationFrame(function () {
        openSizeDrawerForForm(formId);
      });
      return;
    }

    openSizeDrawerForForm(formId);
  }

  function openSizeDrawerForForm(formId) {
    var picker = document.querySelector('variant-picker[form-id="' + formId + '"]');
    var trigger = picker && picker.querySelector('[data-product-size-drawer-trigger]');

    if (trigger) {
      trigger.click();
      return;
    }

    var radio = document.querySelector('.drawer--pdp-size .pdp-size-drawer__radio[form="' + formId + '"]');
    var drawer = radio && radio.closest('.drawer--pdp-size');
    if (drawer && typeof drawer.show === 'function') {
      drawer.show();
      return;
    }

    // Inline size layout: there is no drawer — scroll to the sizes and show the browser bubble.
    var sizeFieldset = picker && picker.querySelector('[data-pdp-size-option]');
    if (sizeFieldset) {
      showBrowserSizePrompt(sizeFieldset, pendingSizeMessage(formId));
    }
  }

  function formIdForSizeOptionNode(node) {
    var fieldset = node.closest('[data-pdp-size-option]');
    if (!fieldset) return null;

    var input = fieldset.querySelector('input[data-option-position][form]');
    if (input) return input.getAttribute('form');

    var picker = fieldset.closest('variant-picker[form-id]');
    return picker ? picker.getAttribute('form-id') : null;
  }

  function formIdForDrawerNode(node) {
    var list = node.closest('[data-pdp-size-drawer-list]');
    var radio = list && list.querySelector('.pdp-size-drawer__radio[form]');
    return radio ? radio.getAttribute('form') : null;
  }

  // The button handler is UX only — it gives the tap something to do instead of a dead
  // disabled button. The submit handler below is the actual safety net.
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;

      var submitButton = event.target.closest('button[type="submit"]');
      if (!submitButton) return;

      var buyButtons = submitButton.closest('buy-buttons');
      var formId = buyButtons && buyButtons.getAttribute('form');
      if (!formId || !isGatePending(formId)) return;

      event.preventDefault();

      promptForSize(formId);
    },
    true,
  );

  // Covers the keyboard Enter key and any third-party button injected into the form.
  // ProductForm calls preventDefault() itself and adds to the cart regardless, so
  // propagation has to be stopped rather than just the default prevented.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      var formId = form.getAttribute('id');
      if (!formId || !isGatePending(formId)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      promptForSize(formId);
    },
    true,
  );

  // Clearing on click rather than on change is deliberate: the first size is already
  // checked server-side, so picking it fires no change event and no re-render, which would
  // otherwise leave the gate closed forever on whichever size happens to be variant #1.
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    var drawerRow = event.target.closest('.pdp-size-drawer__row');
    if (drawerRow) {
      if (drawerRow.classList.contains('pdp-size-drawer__row--sold-out')) return;

      var drawerInput = drawerRow.querySelector('.pdp-size-drawer__radio');
      if (drawerInput && drawerInput.disabled) return;

      var drawerFormId = formIdForDrawerNode(drawerRow);
      if (!isGatePending(drawerFormId)) return;

      resolveGate(drawerFormId);

      // Same reason: without a change event nothing else closes the drawer or marks the row,
      // so do it here once the click has finished and any real change has had its turn.
      window.requestAnimationFrame(function () {
        syncRowSelectedStates(drawerRow.closest('[data-pdp-size-drawer-list]'));
        if (drawerInput) syncTriggerFromInput(drawerInput);
        closeOpenSizeDrawers();
      });
      return;
    }

    // Inline size swatches / blocks — also pre-checked for variant #1.
    var sizeFieldset = event.target.closest('[data-pdp-size-option]');
    if (!sizeFieldset) return;

    var sizeFormId = formIdForSizeOptionNode(sizeFieldset);
    if (!isGatePending(sizeFormId)) return;

    var sizeInput = event.target.closest('input[data-option-position]');
    if (!sizeInput && event.target.closest('label')) {
      var label = event.target.closest('label');
      var forId = label.getAttribute('for');
      sizeInput = forId ? document.getElementById(forId) : label.querySelector('input[data-option-position]');
    }

    if (sizeInput && sizeInput.disabled) return;
    if (sizeInput || event.target.closest('label, a, .block-swatch, .color-swatch')) {
      resolveGate(sizeFormId);
    }
  });

  function bindProductRerenderForms(scope) {
    (scope || document).querySelectorAll('product-rerender[observe-form]').forEach(function (rerenderEl) {
      var formId = rerenderEl.getAttribute('observe-form');
      var form = formId && document.getElementById(formId);
      if (!form || form.dataset.pdpSizeDrawerBound === 'true') return;

      form.dataset.pdpSizeDrawerBound = 'true';

      // Runs synchronously after <product-rerender> has swapped the markup in — the script is
      // deferred, so this listener is always registered after theirs — which re-asserts the gate
      // before the browser paints the freshly rendered buy buttons.
      form.addEventListener('product:rerender', function () {
        refreshGates();

        window.requestAnimationFrame(function () {
          syncDrawerLists(rerenderEl);
          bindProductRerenderForms(document);
          refreshGates();
        });
      });
    });
  }

  function init(scope) {
    syncDrawerLists(scope);
    bindProductRerenderForms(scope);

    (scope || document).querySelectorAll('[data-pdp-size-drawer-list]').forEach(syncRowSelectedStates);
    refreshGates();
  }

  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      if (input.classList.contains('pdp-size-drawer__radio')) {
        var list = input.closest('[data-pdp-size-drawer-list]');
        syncRowSelectedStates(list);
        syncTriggerFromInput(input);
        resolveGate(input.getAttribute('form'));
        closeOpenSizeDrawers();
        return;
      }

      if (input.matches('input[data-option-position]') && input.closest('[data-pdp-size-option]')) {
        resolveGate(input.getAttribute('form') || formIdForSizeOptionNode(input));
      }
    },
    true,
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
      bindQuickBuyModals(document);
    });
  } else {
    init(document);
    bindQuickBuyModals(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    init(event.target);
    bindQuickBuyModals(event.target);
  });

  // Quick shop injects markup after fetch. dialog:after-show does not bubble, so bind per modal.
  var quickBuyFormIds = new Set();

  function bindQuickBuyModals(scope) {
    (scope || document).querySelectorAll('quick-buy-modal').forEach(function (modal) {
      if (modal.dataset.sizeGateBound === 'true') return;
      modal.dataset.sizeGateBound = 'true';

      modal.addEventListener('dialog:after-show', function () {
        quickBuyFormIds.clear();

        modal.querySelectorAll('buy-buttons[form]').forEach(function (buyButtons) {
          var formId = buyButtons.getAttribute('form');
          if (!formId) return;

          quickBuyFormIds.add(formId);

          if (buyButtons.hasAttribute('data-size-pending')) {
            gateState.set(formId, 'pending');
          }
        });

        init(modal);
      });

      modal.addEventListener('dialog:after-hide', function () {
        quickBuyFormIds.forEach(function (formId) {
          gateState.delete(formId);
        });
        quickBuyFormIds.clear();
      });
    });
  }
})();
